<template>
  <Modal
    v-model:visible="isVisible"
    :header="t('lineItems.lineItems', 2)"
    class="line-items-modal"
    size="md"
    no-scroll
    @keypress.esc="onDone"
  >
    <mark class="stop-info">
      <div class="stop">
        <PickupIcon v-if="isPickup" />
        <DropoffIcon v-else />

        <span class="visually-hidden">{{ stopTypeLabel }}</span>

        <NamedAddress
          :name="stop.location.locationName"
          :address="stop.location.toAddress()"
          suppress-country
        />
      </div>

      <LocationCardDate
        :label="appointmentLabel"
        :date="appointmentDateTime"
        use-placeholder
      />
    </mark>

    <LineItemSummaryCard :line-items="lineItems" />

    <div class="ship-orders">
      <Panel
        v-for="({ shipOrder, shipOrderLineItems }, index) in groupedLineItems"
        :id="generatePanelId(shipOrder)"
        :key="shipOrder.id"
        :collapsed="index > 0"
        toggleable
      >
        <template #header>
          <div
            class="flex flex-gap-1 justify-content-between align-items-center w-full"
          >
            <div
              class="header-left flex align-items-center"
              :style="{ gap: 'var(--sp-2)' }"
            >
              <h3>
                {{
                  t('common.concatParams', {
                    param1: t('shipOrders.shipOrders'),
                    param2: shipOrder.compositeId,
                  })
                }}
              </h3>

              <RygenButton
                mode="tertiary"
                :icon="PrimeIcons.MINUS_CIRCLE"
                :aria-label="
                  t('common.removeParam', {
                    param: t('shipOrders.shipOrderNumParam', {
                      param: shipOrder.compositeId,
                    }),
                  })
                "
                :severity="Severity.DANGER"
                @click="emit('remove-ship-order', { shipOrder })"
              />
            </div>

            <div class="header-right flex flex-gap-1 align-items-center">
              <span>
                {{
                  translateNumberedEntity(
                    shipOrderLineItems.length,
                    'lineItems.lineItems',
                  )
                }}
              </span>
            </div>
          </div>
        </template>

        <template #toggleicon="{ collapsed }">
          <span
            class="p-button-icon pi"
            :class="{
              'pi-chevron-down': collapsed,
              'pi-chevron-up': !collapsed,
            }"
          />
        </template>

        <template #default>
          <LineItemViewEdit
            v-for="(lineItem, innerIndex) in shipOrderLineItems"
            :key="innerIndex"
            :model-value="lineItem"
            :index="innerIndex"
          />
        </template>
      </Panel>
    </div>

    <template #footer>
      <RygenButton mode="tertiary" :label="t('common.done')" @click="onDone" />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { translateNumberedEntity } from '@rygen/rygen-ui/i18n';
import { Severity } from '@rygen/rygen-ui/model';

import { ShipmentStopType } from '@/model';
import {
  ProposedShipmentLineItem,
  ProposedShipmentStop,
  RemoveShipOrderFromShipmentPayload,
  ValidShipOrder,
} from '@/model/routeBuilder';
import { RoutePlanStop } from '@/model/routeBuilder/phase2';

import LineItemSummaryCard from '@/components/lineItem/LineItemSummaryCard.vue';
import LineItemViewEdit from '@/components/lineItem/LineItemViewEdit.vue';
import LocationCardDate from '@/components/location/LocationCardDate.vue';
import DropoffIcon from '@/components/shippingPoint/DropoffIcon.vue';
import PickupIcon from '@/components/shippingPoint/PickupIcon.vue';
import NamedAddress from '@rygen/rygen-ui/components/address/NamedAddress.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import Panel from 'primevue/panel';

export type RemoveShipOrderPayload = Pick<
  RemoveShipOrderFromShipmentPayload,
  'shipOrder'
>;

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  lineItems: {
    type: Array as PropType<ProposedShipmentLineItem[]>,
    default: () => [],
  },
  shipOrders: {
    type: Array as PropType<ValidShipOrder[]>,
    default: () => [],
  },
  stop: {
    type: Object as PropType<ProposedShipmentStop | RoutePlanStop>,
    required: true,
  },
  stopTypeLabel: {
    type: String,
    default: () => '',
  },
});
const { lineItems, shipOrders, stop, stopTypeLabel } = toRefs(props);
const emit = defineEmits<{
  (event: 'remove-ship-order', payload: RemoveShipOrderPayload): void;
}>();

const { t } = useI18n();

const isPickup = computed(() => {
  return stop.value.stopType === ShipmentStopType.PICKUP;
});

const appointmentLabel = computed(() => {
  return t('common.paramDates', {
    param: isPickup.value ? t('shipments.pickups') : t('shipments.dropoffs'),
  });
});

const appointmentDateTime = computed(() => {
  return stop.value.requestedAppointmentEarliest;
});

const lineItemsByShipOrderId = computed(() => {
  return Object.groupBy(lineItems.value, (l) => l.shipOrderId);
});

const groupedLineItems = computed(() => {
  return shipOrders.value.map((so) => {
    const lineItems = lineItemsByShipOrderId.value[so.id] ?? [];

    return {
      shipOrder: so,
      shipOrderLineItems: lineItems,
    };
  });
});

const generatePanelId = (shipOrder: ValidShipOrder) => {
  return `ship-order-${shipOrder.id}-line-items-panel`;
};

const onDone = () => {
  isVisible.value = false;
};
</script>

<style>
.line-items-modal {
  --p-panel-background: var(--p-surface-100);
  --p-panel-header-background: white;
  --p-panel-header-border-width: 0 0 1px 0;
  --p-panel-header-border-color: var(--divider-color);
  --p-panel-header-border-radius: var(--p-border-radius-sm);

  .stop-info {
    display: flex;
    gap: var(--gap-3);
    justify-content: center;
    align-items: start;
    background-color: initial;
    color: initial;
    margin-bottom: var(--sp-6);

    .stop {
      display: flex;
      gap: var(--gap-1);
    }

    .location-card-date {
      .key-value-pair-label {
        font-size: 0.9rem;
      }
    }
  }

  .ship-orders {
    display: flex;
    flex-direction: column;
    gap: var(--gap-1);
    margin-top: var(--sp-9);
    overflow: auto;
    max-height: 60vh;

    .p-panel {
      .p-panel-header {
        gap: var(--gap-1);
      }

      .p-panel-content {
        display: flex;
        flex-direction: column;
        gap: var(--gap-1);

        .line-item-view {
          background-color: white;
          border: 1px solid var(--divider-color);

          .line-item-view-row {
            /*
          This is down here instead of up top so it gets the specificity of
          multiple class selectors
          */
            --line-item-view-row-background-color: white;
          }
        }
      }
    }
  }
}
</style>
